import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import SEO from '../../components/SEO';
import ShareButtons from '../../components/ShareButtons';
import { PUBLIC_BASE } from './config';
import ContentCommentSection from '../../components/public/ContentCommentSection';

const LOGO_URL =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/logo.png';

function formatDuration(sec) {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function YouTubeIcon({ className }) {
  return (
    <svg viewBox="0 0 28 20" className={className} fill="currentColor">
      <path d="M27.9727 3.12324C27.6435 1.89323 26.6768 0.926623 25.4468 0.597366C23.2197 2.24288e-07 14.285 0 14.285 0C14.285 0 5.35042 2.24288e-07 3.12323 0.597366C1.89323 0.926623 0.926623 1.89323 0.597366 3.12324C2.24288e-07 5.35042 0 10 0 10C0 10 2.24288e-07 14.6496 0.597366 16.8768C0.926623 18.1068 1.89323 19.0734 3.12323 19.4026C5.35042 20 14.285 20 14.285 20C14.285 20 23.2197 20 25.4468 19.4026C26.6768 19.0734 27.6435 18.1068 27.9727 16.8768C28.5701 14.6496 28.5701 10 28.5701 10C28.5701 10 28.5677 5.35042 27.9727 3.12324Z" fill="white"/>
      <path d="M11.4253 14.2854L18.8477 10.0004L11.4253 5.71533V14.2854Z" fill="#FF0000"/>
    </svg>
  );
}

const DESC_COLLAPSED_LINES = 5;

function PublicMusicVideoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mv, setMv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    axios
      .get(`/public/music-videos/${id}`)
      .then((res) => setMv(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] gap-3 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
          <span>로딩 중...</span>
        </div>
      </PublicLayout>
    );
  }

  if (!mv) {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4 text-slate-500">
          <p>영상을 찾을 수 없습니다.</p>
          <button
            onClick={() => navigate(`${PUBLIC_BASE}/music-videos`)}
            className="text-sky-400 hover:text-sky-300 text-sm">
            목록으로 돌아가기
          </button>
        </div>
      </PublicLayout>
    );
  }

  const descLines = mv.description ? mv.description.split('\n') : [];
  const needsExpand = descLines.length > DESC_COLLAPSED_LINES;
  const visibleDesc = descExpanded
    ? mv.description
    : descLines.slice(0, DESC_COLLAPSED_LINES).join('\n');

  return (
    <PublicLayout>
      <SEO
        title={mv.title || '뮤직비디오'}
        description={`${mv.artist || ''} - ${mv.title || '뮤직비디오'}`}
        path={`/music-videos/${id}`}
        image={mv.thumbnail}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        {/* 뒤로가기 */}
        <button
          onClick={() => navigate(`${PUBLIC_BASE}/music-videos`)}
          className="flex items-center gap-1.5 px-3 py-1.5 mb-5 bg-white/5 hover:bg-white/10 backdrop-blur-sm rounded-full text-slate-300 text-sm transition-colors">
          <ArrowLeft size={14} />
          목록
        </button>

        {/* YouTube Embed */}
        <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${mv.youtubeVideoId}?rel=0`}
            title={mv.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* 메타 정보 */}
        <div className="mt-5 mb-6">
          <h1 className="text-white text-xl sm:text-2xl font-bold leading-tight mb-2">
            {mv.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {mv.artistId ? (
              <button
                onClick={() => navigate(`${PUBLIC_BASE}/artists/${mv.artistId}`)}
                className="text-slate-300 font-medium hover:text-sky-400 transition-colors">
                {mv.artist}
              </button>
            ) : (
              <span className="text-slate-300 font-medium">{mv.artist}</span>
            )}
            {mv.duration && (
              <span className="flex items-center gap-1 text-slate-500 text-xs">
                <Clock size={12} />
                {formatDuration(mv.duration)}
              </span>
            )}
            <ShareButtons
              title={mv.title}
              description={`${mv.artist} - ${mv.title}`}
              image={mv.thumbnail}
            />
            {mv.source === 'internal' ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400">
                <img src={LOGO_URL} alt="p!nk" className="h-[11px] w-auto" />
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400">
                <YouTubeIcon className="w-[16px] h-[12px]" />
                YouTube
              </span>
            )}
          </div>
        </div>

        {/* 영상 설명 */}
        {mv.description && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5">
            <h2 className="text-white text-sm font-semibold mb-3">영상 설명</h2>
            <pre className="text-slate-400 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap font-sans break-words">
              {visibleDesc}
            </pre>
            {needsExpand && (
              <button
                onClick={() => setDescExpanded((v) => !v)}
                className="flex items-center gap-1 mt-3 text-xs text-sky-400 hover:text-sky-300 transition-colors">
                {descExpanded ? (
                  <>
                    <ChevronUp size={14} />
                    접기
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} />
                    더보기
                  </>
                )}
              </button>
            )}
          </div>
        )}
        <ContentCommentSection targetType="musicVideo" targetId={id} />
      </div>
    </PublicLayout>
  );
}

export default PublicMusicVideoDetailPage;
